import Dexie, { Table } from "dexie";

export class DB<T extends Record<string, any>> extends Dexie {
  [key: string]: Table<any, any> | any;

  constructor(dbName: string, tableSchema: Record<keyof T, string>) {
    super(dbName);

    if (!dbName || typeof dbName !== "string") {
      throw new Error("数据库名称必须是非空字符串");
    }
    if (
      !tableSchema ||
      typeof tableSchema !== "object" ||
      Object.keys(tableSchema).length === 0
    ) {
      throw new Error("表结构必须是包含至少一个表的对象");
    }

    this.version(1).stores(tableSchema);
    this.tableNames = Object.keys(tableSchema);

    Object.keys(tableSchema).forEach((tableName) => {
      this[tableName] = this.table(tableName);
    });

    this.on("populate", () => {
      console.log(
        `数据库 [${dbName}] 初始化成功，包含表：${Object.keys(tableSchema).join(
          ", "
        )}`
      );
    });

    // 添加数据库打开成功的日志
    this.open()
      .then(() => {
        console.log(`数据库 [${dbName}] 打开成功`);
      })
      .catch((error) => {
        console.error(`数据库 [${dbName}] 打开失败:`, error);
      });
  }

  async query(tableName: string, condition: any = {}): Promise<any> {
    this._checkTableExist(tableName as string);
    try {
      if (!condition || Object.keys(condition).length === 0) {
        return await this[tableName as any].toArray();
      } else {
        let query = this[tableName as any];
        // 遍历条件，拼接查询逻辑（多条件为 AND 关系）
        Object.entries(condition).forEach(([key, value]) => {
          // 使用 filter 兼容无索引字段（避免 SchemaError）
          query = query.filter((item: any) => item[key as keyof any] === value);
        });
        // 获取所有匹配的数据（替换 first() 为 toArray()）
        const result = await query.toArray();
        console.log(
          `${String(tableName)} 表按条件查询到 ${result.length} 条数据`
        );
        return result;
      }
    } catch (error) {
      console.error(`${String(tableName)} 表按条件查询失败：`, error);
      throw error;
    }
  }

  async remove(tableName: string, condition: any = {}): Promise<any> {
    this._checkTableExist(tableName as string);
    try {
      if (!condition || Object.keys(condition).length === 0) {
        await this[tableName as any].clear();
        return null;
      } else {
        const target = await (
          this[tableName as any].where(condition) as any
        ).delete();
        return target;
      }
    } catch (error) {
      console.error(`删除 ${String(tableName)} 表所有数据失败：`, error);
      throw error;
    }
  }

  async insert(tableName: string, data: any | any[]): Promise<any | any[]> {
    this._checkTableExist(tableName as string);
    if (!data || (Array.isArray(data) && data.length === 0)) {
      throw new Error("插入的数据不能为空");
    }
    try {
      let result: any | any[];
      if (Array.isArray(data)) {
        result = await this[tableName as any].bulkAdd(data);
        console.log(
          `${String(tableName)} 表批量插入 ${data.length} 条数据成功`
        );
      } else {
        result = await this[tableName as any].add(data);
        console.log(`${String(tableName)} 表插入单条数据成功，主键：${result}`);
      }
      return result;
    } catch (error: any) {
      if (error.name === "ConstraintError") {
        console.error(`${String(tableName)} 表插入数据失败：主键重复`, error);
      } else {
        console.error(`${String(tableName)} 表插入数据失败：`, error);
      }
      throw error;
    }
  }

  async update(
    tableName: string,
    data: any,
    condition: any = {}
  ): Promise<any> {
    this._checkTableExist(tableName as string);
    if (condition == null || Object.keys(condition).length === 0) {
      throw new Error("更新的条件不能为空");
    }
    if (!data || Object.keys(data).length === 0) {
      throw new Error("更新的数据不能为空");
    }
    try {
      const matchedData = await this.query(tableName, condition);
      if (matchedData.length > 0) {
        // 存在则更新（更新第一条匹配的数据，也可根据需求调整为批量更新）
        const targetId = matchedData[0].id; // 假设主键字段为 id（Dexie 默认主键）
        await this[tableName].update(targetId, data);
        console.log(`${String(tableName)} 表ID为 ${targetId} 的数据已更新`);
        return targetId;
      } else {
        throw new Error(`未找到符合条件的数据`);
      }
    } catch (error) {
      console.error(
        `更新 ${String(tableName)} 表 条件为 ${condition} 的数据失败：`,
        error
      );
      throw error;
    }
  }

  async insertOrUpdate(
    tableName: string,
    data: any, // 要插入/更新的数据
    condition: any // 查询条件（判断是否存在的依据）
  ): Promise<{ type: "insert" | "update"; id?: any }> {
    this._checkTableExist(tableName as string);

    // 校验参数
    if (!data || Object.keys(data).length === 0) {
      throw new Error("插入/更新的数据不能为空");
    }
    if (!condition || Object.keys(condition).length === 0) {
      throw new Error("查询条件不能为空（用于判断数据是否存在）");
    }

    try {
      const matchedData = await this.query(tableName, condition);
      if (matchedData.length > 0) {
        const updatedId = this.update(tableName, data, condition);
        return { type: "update", id: updatedId };
      } else {
        const insertId = await this.insert(tableName, data);
        return { type: "insert", id: insertId };
      }
    } catch (error) {
      console.error(`${String(tableName)} 表执行 insertOrUpdate 失败：`, error);
      throw error;
    }
  }

  private _checkTableExist(tableName: string): void {
    if (!this.tableNames.includes(tableName)) {
      throw new Error(`数据库中不存在表 [${tableName}]`);
    }
  }

  async removebase(): Promise<void> {
    try {
      const dbName = this.name;
      this.close();
      await Dexie.delete(dbName);
      console.log(`数据库 [${dbName}] 已成功删除`);
    } catch (error) {
      console.error(`删除数据库 [${this.name}] 失败：`, error);
      throw error;
    }
  }
}

// 电商会计
export const ec = new DB("ec", {
  shop: "id, shopName,  description, platform, productLoaded, orderLoaded, fundLoaded, date",
  product:
    "[商品编码+规格编码+店铺编号], 店铺编号, 商品编码, 商品名称, 规格型号, 规格编码, 单件成本",
  order:
    "[订单编号+店铺编号],订单编号,店铺编号, 商品编码, 规格编码, 确认时间, 商家实收金额",
  fund: "[关联订单号+店铺编号+发生时间+业务类型],  关联订单号,店铺编号, 收入, 支出, 发生时间, 业务类型, 业务详情",
});

// 管理会计
export const ma = new DB("ma", {
  sales_history: "++id, time, sales, trend, season",
  sales_forecast: "++id, time, trend, season, forecastValue",
});

/*

export async function exportDatabaseToExcel() {
  try {
    await db.open();

    const tableNames = db.tables.map((table) => table.name);
    // return;
    const workbook = XLSX.utils.book_new();

    for (const tableName of tableNames) {
      const data = await db.table(tableName).toArray();
      if (data.length > 0) {
        const headers = Object.keys(data[0]);
        const rows = data.map((item) => headers.map((header) => item[header]));
        rows.unshift(headers);

        const worksheet = XLSX.utils.aoa_to_sheet(rows);
        XLSX.utils.book_append_sheet(workbook, worksheet, tableName);
      }
    }

    XLSX.writeFile(workbook, 'database_export.xlsx');
  } catch (error) {
    console.error('导出数据时出错:', error);
  }
}

export async function importExcelToIndexDB(file: Blob) {
  try {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);

    await new Promise((resolve: Function, reject: Function) => {
      reader.onload = async (e: any) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        for (const sheetName of workbook.SheetNames) {
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          const headers = jsonData[0];
          const rows = jsonData.slice(1);

          const tableSchema = (headers as any[]).map((header) => header).join(',');

          await db.open();

          const table = db.table(sheetName);

          await table.clear();

          const newData = rows.map((row: any) => {
            const item = {} as any;
            (headers as any[]).forEach((header: string, index: number) => {
              item[header.trim()] = row[index];
            });
            return item;
          });

          await table.bulkAdd(newData);
        }

        resolve();
      };

      reader.onerror = (error) => {
        reject(error);
      };
    });

    console.log('数据导入成功');
  } catch (error) {
    console.error('数据导入失败:', error);
  }
}

*/
